import { Link, createFileRoute } from "@tanstack/react-router";
import { PROFILE_CATEGORIES } from "../data/wb/profile/categories";
import { POPULATION_HEADLINE } from "../data/wb/profile/population-highlights";
import { SHIRES } from "../components/shared/electorate-shires";
import { SHIRE_PROFILES } from "../data/shire-profiles";
import { Breadcrumbs } from "../components/shared/Breadcrumbs";



export const Route = createFileRoute("/profile/")({
  component: ProfileIndex,
  head: () => ({
    meta: [
      { title: "Community profile — Warren-Blackwood" },
      {
        name: "description",
        content:
          "Demographic profile of the Warren-Blackwood electorate: population, age, income, housing and more, benchmarked against Greater Perth, WA and Australia.",
      },
      { property: "og:title", content: "Community profile — Warren-Blackwood" },
      {
        property: "og:description",
        content:
          "Census-derived demographic profile of Warren-Blackwood, benchmarked against WA and Australia.",
      },
    ],
  }),
});

function ProfileIndex() {
  // The hero card carries the single primary claim. The remaining categories
  // become a flat, sorted grid below — implemented first (alpha), then "soon".
  const others = PROFILE_CATEGORIES.filter((c) => c.slug !== "population" && !c.hidden);
  const sorted = [
    ...others.filter((c) => c.implemented).sort((a, b) => a.title.localeCompare(b.title)),
    ...others.filter((c) => !c.implemented).sort((a, b) => a.title.localeCompare(b.title)),
  ];

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 960,
        margin: "0 auto",
        padding: "var(--sp-lg) var(--sp-md) var(--sp-xl)",
      }}
    >

        <Breadcrumbs items={[{ label: "Map", to: "/" }, { label: "Community profile", to: "/profile" }]} />

        <p
          style={{
            fontFamily: "var(--font-sub)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontSize: "0.75rem",
            color: "var(--rivergum)",
            margin: 0,
            marginBottom: "0.35rem",
          }}
        >
          Warren-Blackwood, by the numbers
        </p>
        <h1
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "clamp(2rem, 5vw, 2.75rem)",
            color: "var(--rivergum)",
            margin: "0 0 var(--sp-md)",
            lineHeight: 1.05,
          }}
        >
          Community profile
        </h1>


        <PopulationHero />

        <PopulationByShire />

        <KeyStatsByShire />

        <CategoryGrid
          heading="More categories"
          subheading="Pick a category to see Warren-Blackwood benchmarked against Greater Perth, WA and Australia."
          items={sorted}
        />
    </main>
  );
}


function PopulationHero() {
  const stats: { value: string; label: string; sub: string }[] = [
    {
      value: POPULATION_HEADLINE.erp2025.toLocaleString(),
      label: "Residents",
      sub: "Estimated, 2025",
    },
    {
      value: `${POPULATION_HEADLINE.landAreaKm2.toLocaleString()} km²`,
      label: "Land area",
      sub: "WA's south-west",
    },
    {
      value: String(SHIRES.length),
      label: "Shires",
      sub: "From Margaret River to Denmark",
    },
  ];

  return (
    <Link
      to="/profile/$slug"
      params={{ slug: "population" }}
      aria-label="Open the full population highlights category"
      style={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        background: "var(--rivergum)",
        borderRadius: 10,
        padding: "var(--sp-lg) var(--sp-md)",
        marginBottom: "var(--sp-lg)",
      }}
    >
      <p
        style={{
          fontFamily: "var(--font-sub)",
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          fontSize: "0.7rem",
          color: "var(--yellow)",
          margin: 0,
          marginBottom: "var(--sp-sm)",
        }}
      >
        Population highlights
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "var(--sp-md)",
          marginBottom: "var(--sp-md)",
        }}
      >
        {stats.map((s) => (
          <div key={s.label}>
            <div
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(2.25rem, 6vw, 3.5rem)",
                color: "var(--warm-white)",
                lineHeight: 1,
                marginBottom: "0.35rem",
              }}
            >
              {s.value}
            </div>
            <div
              style={{
                fontFamily: "var(--font-sub)",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontSize: "0.75rem",
                color: "var(--yellow)",
                marginBottom: "0.2rem",
              }}
            >
              {s.label}
            </div>
            <div
              style={{
                fontSize: "0.8rem",
                color: "var(--dusty-sage)",
              }}
            >
              {s.sub}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          fontSize: "0.85rem",
          color: "var(--warm-white)",
          textDecoration: "underline",
          textDecorationColor: "var(--yellow)",
          textUnderlineOffset: 3,
          textDecorationThickness: 2,
        }}
      >
        Full population highlights →
      </div>
    </Link>
  );
}

function KeyStatsByShire() {
  const data = [
    {
      name: "Augusta-Margaret River",
      slug: "augusta-margaret-river",
      profile: SHIRE_PROFILES["augusta-margaret-river"],
    },
    { name: "Manjimup", slug: "manjimup", profile: SHIRE_PROFILES["manjimup"] },
    { name: "Denmark", slug: "denmark", profile: SHIRE_PROFILES["denmark"] },
    {
      name: "Bridgetown-Greenbushes",
      slug: "bridgetown-greenbushes",
      profile: SHIRE_PROFILES["bridgetown-greenbushes"],
    },
    { name: "Boyup Brook", slug: "boyup-brook", profile: SHIRE_PROFILES["boyup-brook"] },
    { name: "Nannup", slug: "nannup", profile: SHIRE_PROFILES["nannup"] },
  ];

  const popLabel = "Population (2021)";
  const ageLabel = "Median age";
  const incomeLabel = "Median weekly household income";

  return (
    <section style={{ marginBottom: "var(--sp-lg)" }}>
      <h2
        style={{
          fontFamily: "var(--font-heading)",
          fontSize: "1.4rem",
          color: "var(--rivergum)",
          marginBottom: "0.35rem",
          paddingBottom: "0.3rem",
          borderBottom: "1px solid var(--light-border)",
        }}
      >
        Key stats by shire
      </h2>
      <p
        style={{
          fontSize: "0.8rem",
          color: "var(--ink-mid)",
          margin: "0 0 var(--sp-sm)",
          fontStyle: "italic",
        }}
      >
        ABS 2021 Census QuickStats.
      </p>
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.9rem",
          }}
        >
          <thead>
            <tr style={{ borderBottom: "2px solid var(--rule)" }}>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.5rem 0.75rem",
                  fontWeight: 600,
                  color: "var(--rivergum)",
                }}
              >
                Shire
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.5rem 0.75rem",
                  fontWeight: 600,
                  color: "var(--rivergum)",
                }}
              >
                Population
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.5rem 0.75rem",
                  fontWeight: 600,
                  color: "var(--rivergum)",
                }}
              >
                Median age
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.5rem 0.75rem",
                  fontWeight: 600,
                  color: "var(--rivergum)",
                }}
              >
                Median household income
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: "0.5rem 0.75rem",
                  fontWeight: 600,
                  color: "var(--rivergum)",
                }}
              >
                % who own their home
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((s) => {
              const pop = s.profile.headline.find((h) => h.label === popLabel)?.value ?? "—";
              const age = s.profile.headline.find((h) => h.label === ageLabel)?.value ?? "—";
              const income = s.profile.headline.find((h) => h.label === incomeLabel)?.value ?? "—";
              return (
                <tr
                  key={s.slug}
                  style={{ borderBottom: "1px solid var(--light-border)" }}
                >
                  <td style={{ padding: "0.5rem 0.75rem" }}>
                    <Link
                      to="/shire/$name"
                      params={{ name: s.slug }}
                      style={{
                        color: "var(--rivergum)",
                        textDecoration: "underline",
                        textDecorationColor: "var(--yellow)",
                        textUnderlineOffset: "2px",
                        textDecorationThickness: "1.5px",
                      }}
                    >
                      {s.name}
                    </Link>
                  </td>
                  <td
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {pop}
                  </td>
                  <td
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {age}
                  </td>
                  <td
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {income}/wk
                  </td>
                  <td
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {s.profile.tenure.ownedOutright}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CategoryGrid({
  heading,
  subheading,
  items,
}: {
  heading: string;
  subheading?: string;
  items: typeof PROFILE_CATEGORIES;
}) {
  return (
    <section style={{ marginBottom: "var(--sp-lg)" }}>
      <h2
        style={{
          fontFamily: "var(--font-heading)",
          fontSize: "1.4rem",
          color: "var(--rivergum)",
          marginBottom: "0.35rem",
          paddingBottom: "0.3rem",
          borderBottom: "1px solid var(--light-border)",
        }}
      >
        {heading}
      </h2>
      {subheading ? (
        <p
          style={{
            color: "var(--ink-mid)",
            fontSize: "0.9rem",
            margin: 0,
            marginBottom: "var(--sp-sm)",
          }}
        >
          {subheading}
        </p>
      ) : null}

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: "var(--sp-sm)",
        }}
      >
        {items.map((cat) => {
          const body = (
            <>
              <h3
                style={{
                  margin: 0,
                  marginBottom: "0.3rem",
                  color: cat.implemented ? "var(--off-black)" : "var(--mid-grey)",
                }}
              >
                {cat.title}
                {!cat.implemented ? (
                  <span
                    style={{
                      marginLeft: "0.4rem",
                      fontSize: "0.65rem",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--mid-grey)",
                      fontWeight: 600,
                    }}
                  >
                    soon
                  </span>
                ) : null}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.88rem",
                  color: "var(--ink-mid)",
                  lineHeight: 1.4,
                }}
              >
                {cat.summary}
              </p>
            </>
          );

          return (
            <li
              key={cat.slug}
              style={{
                background: cat.implemented ? "var(--warm-white)" : "var(--fleece)",
                border: "1px solid var(--light-border)",
                borderLeft: `3px solid ${cat.implemented ? "var(--yellow)" : "var(--light-border)"}`,
                borderRadius: 6,
              }}
            >
              {cat.implemented ? (
                <Link
                  to="/profile/$slug"
                  params={{ slug: cat.slug }}
                  style={{
                    display: "block",
                    padding: "var(--sp-sm)",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  {body}
                </Link>
              ) : (
                <div style={{ padding: "var(--sp-sm)" }}>{body}</div>
              )}
            </li>
          );
        })}
        </ul>
      </section>
    );
  }

function PopulationByShire() {
  const data = [
    { name: "Augusta-Margaret River", pop: 16791, slug: "augusta-margaret-river" },
    { name: "Manjimup", pop: 9093, slug: "manjimup" },
    { name: "Denmark", pop: 6310, slug: "denmark" },
    { name: "Bridgetown-Greenbushes", pop: 5238, slug: "bridgetown-greenbushes" },
    { name: "Boyup Brook", pop: 1834, slug: "boyup-brook" },
    { name: "Nannup", pop: 1538, slug: "nannup" },
  ];
  const maxPop = data[0].pop;

  return (
    <section style={{ marginBottom: "var(--sp-lg)" }}>
      <h2
        style={{
          fontFamily: "var(--font-heading)",
          fontSize: "1.4rem",
          color: "var(--rivergum)",
          marginBottom: "0.35rem",
          paddingBottom: "0.3rem",
          borderBottom: "1px solid var(--light-border)",
        }}
      >
        Population by shire
      </h2>
      <p
        style={{
          fontSize: "0.8rem",
          color: "var(--ink-mid)",
          margin: "0 0 var(--sp-sm)",
          fontStyle: "italic",
        }}
      >
        ABS 2021 Census estimates.
      </p>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: "0.55rem",
        }}
      >
        {data.map((s) => {
          const barWidth = `${(s.pop / maxPop) * 100}%`;
          return (
            <li key={s.slug}>
              <Link
                to="/shire/$name"
                params={{ name: s.slug }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <span
                  style={{
                    minWidth: "11rem",
                    fontSize: "0.88rem",
                    fontWeight: 600,
                    color: "var(--rivergum)",
                    textDecoration: "underline",
                    textDecorationColor: "var(--yellow)",
                    textUnderlineOffset: "2px",
                    textDecorationThickness: "1.5px",
                  }}
                >
                  {s.name}
                </span>
                <span
                  style={{
                    width: "6.5rem",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "0.4rem",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      height: "6px",
                      borderRadius: "3px",
                      background: "var(--yellow)",
                      width: barWidth,
                      minWidth: "2px",
                      opacity: 0.85,
                    }}
                  />
                </span>
                <span
                  style={{
                    fontSize: "0.82rem",
                    color: "var(--ink-mid)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {s.pop.toLocaleString()}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
